import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Trash2, Edit, Users, Loader2, UserCheck, User, Search, CheckCircle, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TeamPage = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [saving, setSaving] = useState(false);

  // Add form state
  const [addEmail, setAddEmail] = useState('');
  const [addName, setAddName] = useState('');
  const [addPassword, setAddPassword] = useState('');
  const [lookupState, setLookupState] = useState('idle'); // idle | looking | found | not_found | already_member
  const [foundUser, setFoundUser] = useState(null);
  const lookupTimeoutRef = useRef(null);

  // Edit form
  const [editData, setEditData] = useState({ name: '', email: '', password: '' });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/team`);
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const resetAddForm = () => {
    setAddEmail('');
    setAddName('');
    setAddPassword('');
    setLookupState('idle');
    setFoundUser(null);
  };

  // Debounced email lookup
  const lookupEmail = useCallback(async (email) => {
    if (!email || !email.includes('@')) {
      setLookupState('idle');
      setFoundUser(null);
      return;
    }
    setLookupState('looking');
    try {
      const res = await axios.get(`${API_URL}/team/lookup?email=${encodeURIComponent(email)}`);
      if (res.data.found) {
        if (res.data.already_member) {
          setLookupState('already_member');
          setFoundUser(res.data);
        } else {
          setLookupState('found');
          setFoundUser(res.data);
          setAddName(res.data.name); // auto-fill
        }
      } else {
        setLookupState('not_found');
        setFoundUser(null);
        setAddName(''); // clear any previous auto-fill
      }
    } catch {
      setLookupState('not_found');
      setFoundUser(null);
    }
  }, []);

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setAddEmail(val);
    setLookupState('idle');
    setFoundUser(null);
    setAddName('');
    if (lookupTimeoutRef.current) clearTimeout(lookupTimeoutRef.current);
    lookupTimeoutRef.current = setTimeout(() => lookupEmail(val.trim()), 500);
  };

  const handleAdd = async () => {
    if (!addEmail) { toast.error('Email is required'); return; }
    if (lookupState === 'already_member') { toast.error('This person is already a team member here'); return; }

    // New user requires name + password
    if (lookupState === 'not_found') {
      if (!addName.trim()) { toast.error('Name is required for new users'); return; }
      if (!addPassword) { toast.error('Password is required for new users'); return; }
    }

    setSaving(true);
    try {
      const payload = { email: addEmail };
      if (lookupState !== 'found') {
        payload.name = addName;
        payload.password = addPassword;
      }
      const response = await axios.post(`${API_URL}/team`, payload);
      setMembers(prev => [...prev, response.data]);
      setIsCreateOpen(false);
      resetAddForm();
      toast.success(lookupState === 'found' ? `${foundUser.name} added to this client` : 'Team member created and added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedMember) return;
    setSaving(true);
    try {
      const updateData = { name: editData.name, email: editData.email };
      if (editData.password) updateData.password = editData.password;
      const response = await axios.put(`${API_URL}/team/${selectedMember.id}`, updateData);
      setMembers(members.map(m => m.id === selectedMember.id ? { ...response.data, is_owner: false } : m));
      setIsEditOpen(false);
      setSelectedMember(null);
      toast.success('Team member updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team member');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (memberId) => {
    if (!window.confirm('Remove this team member from this client? Their account is kept and they can still access other clients they belong to.')) return;
    try {
      await axios.delete(`${API_URL}/team/${memberId}`);
      setMembers(members.filter(m => m.id !== memberId));
      toast.success('Team member removed from this client');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const openEdit = (member) => {
    setSelectedMember(member);
    setEditData({ name: member.name, email: member.email, password: '' });
    setIsEditOpen(true);
  };

  // Lookup status badge
  const LookupBadge = () => {
    if (lookupState === 'looking') return (
      <div className="flex items-center gap-1.5 text-[12px] text-[#8A8A8B]">
        <Loader2 className="w-3 h-3 animate-spin" /> Searching...
      </div>
    );
    if (lookupState === 'found') return (
      <div className="flex items-center gap-1.5 text-[12px] text-[#3BAB59] bg-[#3BAB59]/10 px-2 py-1 rounded">
        <CheckCircle className="w-3 h-3" />
        Existing user found — no password needed
      </div>
    );
    if (lookupState === 'already_member') return (
      <div className="flex items-center gap-1.5 text-[12px] text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
        <UserCheck className="w-3 h-3" />
        Already a member of this client
      </div>
    );
    if (lookupState === 'not_found') return (
      <div className="flex items-center gap-1.5 text-[12px] text-[#595C5C]">
        <UserPlus className="w-3 h-3" />
        New user — enter name and password
      </div>
    );
    return null;
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[30px] font-semibold text-[#131517]">Team</h1>
          <p className="text-[14px] text-[#595C5C] mt-1">Manage team members who can access this account</p>
        </div>
        <Button
          className="bg-[#131517] hover:bg-[#333537]"
          data-testid="add-member-btn"
          onClick={() => { resetAddForm(); setIsCreateOpen(true); }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Member
        </Button>
      </div>

      {/* Members Table */}
      <Card className="border-[#D9E2E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#595C5C]" />
            </div>
          ) : members.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-[#D9E2E5] hover:bg-transparent">
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Member</TableHead>
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Email</TableHead>
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Role</TableHead>
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase">Status</TableHead>
                  <TableHead className="text-[12px] font-semibold text-[#8A8A8B] uppercase text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id} className="border-[#D9E2E5]" data-testid={`member-row-${member.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-[#EFEFEF] rounded-full flex items-center justify-center">
                          {member.is_owner ? (
                            <UserCheck className="w-4 h-4 text-[#3BAB59]" />
                          ) : (
                            <User className="w-4 h-4 text-[#595C5C]" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-[#131517]">{member.name}</p>
                          {member.role && member.role !== 'TEAM_MEMBER' && (
                            <p className="text-[11px] text-[#8A8A8B]">{member.role === 'CLIENT' ? 'Client Owner' : member.role}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#595C5C]">{member.email}</TableCell>
                    <TableCell>
                      {member.is_owner ? (
                        <span className="badge-success">Owner</span>
                      ) : (
                        <span className="badge-neutral">Team Member</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {member.is_active !== false ? (
                        <span className="badge-success">Active</span>
                      ) : (
                        <span className="badge-error">Inactive</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!member.is_owner && (
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(member)}
                            className="h-8 w-8 p-0 text-[#595C5C] hover:text-[#131517]"
                            data-testid={`edit-member-${member.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(member.id)}
                            className="h-8 w-8 p-0 text-[#EB493D] hover:text-[#EB493D] hover:bg-[#EB493D]/10"
                            data-testid={`delete-member-${member.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-[#D9E2E5] mb-3" />
              <p className="text-[14px] text-[#8A8A8B]">No team members yet</p>
              <p className="text-[12px] text-[#AFB0B0] mt-1">Add team members to collaborate</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Member Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) resetAddForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[20px] font-semibold text-[#131517]">Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">

            {/* Email first — triggers lookup */}
            <div className="space-y-2">
              <Label className="text-[#131517]">Email *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A8B]" />
                <Input
                  type="email"
                  value={addEmail}
                  onChange={handleEmailChange}
                  placeholder="Enter email address"
                  className="border-[#D9E2E5] pl-9"
                  data-testid="member-email-input"
                  autoFocus
                />
              </div>
              <LookupBadge />
            </div>

            {/* Name — auto-filled and locked if existing user, editable if new */}
            {(lookupState === 'found' || lookupState === 'not_found') && (
              <div className="space-y-2">
                <Label className="text-[#131517]">
                  Name {lookupState === 'not_found' && <span className="text-[#EB493D]">*</span>}
                </Label>
                <Input
                  value={addName}
                  onChange={(e) => lookupState === 'not_found' && setAddName(e.target.value)}
                  placeholder="Full name"
                  className={`border-[#D9E2E5] ${lookupState === 'found' ? 'bg-[#F7F8FA] text-[#595C5C]' : ''}`}
                  readOnly={lookupState === 'found'}
                  data-testid="member-name-input"
                />
                {lookupState === 'found' && (
                  <p className="text-[11px] text-[#8A8A8B]">Name is auto-filled from their existing account</p>
                )}
              </div>
            )}

            {/* Password — only for new users */}
            {lookupState === 'not_found' && (
              <div className="space-y-2">
                <Label className="text-[#131517]">Password <span className="text-[#EB493D]">*</span></Label>
                <Input
                  type="password"
                  value={addPassword}
                  onChange={(e) => setAddPassword(e.target.value)}
                  placeholder="Set a password"
                  className="border-[#D9E2E5]"
                  data-testid="member-password-input"
                />
              </div>
            )}

            {/* Existing user info box */}
            {lookupState === 'found' && foundUser && (
              <div className="p-3 bg-[#F7F8FA] border border-[#D9E2E5] rounded-lg text-[12px] text-[#595C5C]">
                <p><span className="font-medium text-[#131517]">{foundUser.name}</span> already has an account.</p>
                <p className="mt-0.5">They will be added to this client and can switch between their accounts after login.</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-[#D9E2E5]">
              <Button variant="outline" onClick={() => { setIsCreateOpen(false); resetAddForm(); }} className="border-[#D9E2E5]">
                Cancel
              </Button>
              <Button
                onClick={handleAdd}
                disabled={saving || lookupState === 'idle' || lookupState === 'looking' || lookupState === 'already_member'}
                className="bg-[#131517] hover:bg-[#333537]"
                data-testid="save-member-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                {lookupState === 'found' ? 'Add Existing User' : 'Create & Add'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-[20px] font-semibold text-[#131517]">Edit Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-[#131517]">Name</Label>
              <Input
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                className="border-[#D9E2E5]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#131517]">Email</Label>
              <Input
                type="email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                className="border-[#D9E2E5]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#131517]">New Password <span className="text-[#8A8A8B] font-normal">(leave blank to keep current)</span></Label>
              <Input
                type="password"
                value={editData.password}
                onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                placeholder="New password"
                className="border-[#D9E2E5]"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-[#D9E2E5]">
              <Button variant="outline" onClick={() => setIsEditOpen(false)} className="border-[#D9E2E5]">
                Cancel
              </Button>
              <Button onClick={handleEdit} disabled={saving} className="bg-[#131517] hover:bg-[#333537]">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPage;
